/**
 * Transient `data-durable-run` part the AI API streams right after `start` when the turn runs on the
 * durable stream. It reaches `useChat`'s `onData` only — never `message.parts`.
 */
export type DurableRunDataPart = { type: 'data-durable-run'; data: { runId: string } };

export const isDurableRunDataPart = (part: { type: string; data: unknown }): part is DurableRunDataPart => {
  if (part.type !== 'data-durable-run' || typeof part.data !== 'object' || part.data === null) return false;
  const { runId } = part.data as Record<string, unknown>;
  return typeof runId === 'string' && runId.length > 0;
};

/** Structural so it fits both the AI SDK v6 messages and Mastra's v5 conversions the chats hold. */
export type ChatMessage = { id: string; role: string; parts: ReadonlyArray<{ type: string }> };

export type DurableRunRecord<M extends ChatMessage = ChatMessage> = {
  runId: string;
  agentName: string;
  userMessage: M;
  /** When the turn was sent: a resume right after it can reach the server before the run is registered. */
  startedAt?: number;
};

// sessionStorage: survives F5 but not the tab, so a second tab on the same thread never resumes the
// run twice and a record orphaned by an unfinished turn dies with the tab instead of lingering.
const STORAGE_PREFIX = 'ai:durable-run:';

export const rememberDurableRun = <M extends ChatMessage>(threadId: string, record: DurableRunRecord<M>) => {
  try {
    window.sessionStorage.setItem(`${STORAGE_PREFIX}${threadId}`, JSON.stringify(record));
  } catch {
    // Quota or privacy mode: the turn still streams, it just won't survive a reload.
  }
};

export const readDurableRun = (threadId: string): DurableRunRecord | null => {
  try {
    const raw = window.sessionStorage.getItem(`${STORAGE_PREFIX}${threadId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DurableRunRecord>;
    if (typeof parsed.runId !== 'string' || typeof parsed.agentName !== 'string' || parsed.userMessage?.role !== 'user') return null;
    return parsed as DurableRunRecord;
  } catch {
    return null;
  }
};

export const forgetDurableRun = (threadId: string) => {
  try {
    window.sessionStorage.removeItem(`${STORAGE_PREFIX}${threadId}`);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
};

/**
 * Body field that names the run before the request leaves, so leaving the thread (or F5) right after
 * sending still finds it to resume. The server may ignore it and announce its own id instead; the
 * send fetch adopts whatever `data-durable-run` carries. An approval reply resumes a run that already
 * exists, so it claims nothing.
 */
export const claimDurableRunId = <M extends ChatMessage>({ threadId, agentName, messages }: { threadId: string; agentName: string; messages: M[] }) => {
  const userMessage = messages.at(-1);
  if (userMessage?.role !== 'user') return {};
  const runId = crypto.randomUUID();
  rememberDurableRun(threadId, { runId, agentName, userMessage, startedAt: Date.now() });
  return { durableRunId: runId };
};

const durableRunStreamUrl = (baseUrl: string, agentName: string, runId: string) =>
  `${baseUrl}/api/v1/chat/${encodeURIComponent(agentName)}/runs/${encodeURIComponent(runId)}/stream`;

/**
 * Resume responses that mean "reload the thread history": run gone, finished, expired, durable off,
 * or someone else's run. `reconnectToStream` turns a 204 into a silent no-op, while any other
 * non-2xx would surface as a chat error.
 */
const silenceResumeFailure = (response: Response, threadId: string) => {
  const onAccepted = acceptedListeners.get(threadId);
  acceptedListeners.delete(threadId);
  if (response.ok) {
    onAccepted?.();
    return response;
  }
  forgetDurableRun(threadId);
  return new Response(null, { status: 204 });
};

const acceptedListeners = new Map<string, () => void>();

/**
 * Runs `listener` once the resume route answers 200, before the SDK reads the chat's last message to
 * build the resumed answer. History must only be trimmed then: on a fallback it stays as loaded.
 */
export const onDurableResumeAccepted = (threadId: string, listener: () => void) => {
  acceptedListeners.set(threadId, listener);
};

type DurableResumeOptions = { baseUrl: string; agentName: string; threadId: string; fetch: typeof fetch };

const abortError = () => new DOMException('The operation was aborted.', 'AbortError');

// A thread left mid-answer keeps reading its stream (sidebar spinner, history refresh at the end) up to this cap.
const DETACHED_TURN_MAX_MS = 10 * 60_000;
// A resume racing the turn's start finds no owner yet (404) or no replay yet (410): both clear up
// within seconds, so a just-sent run is asked again before falling back to the history.
const RESUME_RETRY_WINDOW_MS = 15_000;
const RESUME_RETRY_DELAY_MS = 1_000;
const RESUME_MAX_ATTEMPTS = 8;
const isStartingRun = (status: number) => status === 404 || status === 410;

const respondingTurns = new Map<string, Set<AbortController>>();
let respondingSnapshot: ReadonlySet<string> = new Set();
const respondingListeners = new Set<() => void>();
export type TurnSettled = { kind: 'send' | 'resume' /** No other request of the thread is still reading. */; isLast: boolean };
const settledListeners = new Set<(threadId: string, settled: TurnSettled) => void>();

const publishResponding = () => {
  respondingSnapshot = new Set(respondingTurns.keys());
  for (const listener of respondingListeners) listener();
};

const trackTurn = (threadId: string, network: AbortController) => {
  const turns = respondingTurns.get(threadId) ?? new Set();
  turns.add(network);
  respondingTurns.set(threadId, turns);
  publishResponding();
};

const untrackTurn = (threadId: string, network: AbortController, kind: TurnSettled['kind']) => {
  const turns = respondingTurns.get(threadId);
  if (!turns?.delete(network)) return;
  const isLast = turns.size === 0;
  if (isLast) respondingTurns.delete(threadId);
  publishResponding();
  for (const listener of settledListeners) listener(threadId, { kind, isLast });
};

/** Threads whose answer is still streaming in this tab, including ones the user already left. */
export const subscribeRespondingThreads = (listener: () => void) => {
  respondingListeners.add(listener);
  return () => {
    respondingListeners.delete(listener);
  };
};

export const getRespondingThreads = () => respondingSnapshot;

/** Runs `listener` when a request of a thread's turn ends, even one the user left: its history changed. */
export const onTurnSettled = (listener: (threadId: string, settled: TurnSettled) => void) => {
  settledListeners.add(listener);
  return () => {
    settledListeners.delete(listener);
  };
};

/** Explicit stop: cuts the thread's requests now, even if the run id never arrived, so nothing stores it later. */
export const abortDurableSend = (threadId: string) => {
  for (const network of respondingTurns.get(threadId) ?? []) network.abort();
};

const adoptAnnouncedRunId = (threadId: string, runId: string) => {
  const record = readDurableRun(threadId);
  if (record && record.runId !== runId) rememberDurableRun(threadId, { ...record, runId });
};

// Frames that only come after the run announcement: past them, there is nothing left to learn.
const isPastAnnouncement = (type: string) => type === 'text-start' || type === 'text-delta' || type === 'tool-input-start' || type === 'finish' || type === 'error';

const parseFrame = (line: string): { type: string; data: unknown } | null => {
  if (!line.startsWith('data: ')) return null;
  try {
    const part = JSON.parse(line.slice('data: '.length)) as { type?: unknown; data?: unknown };
    return { type: String(part.type), data: part.data };
  } catch {
    return null;
  }
};

type TurnProgress = { isLearningRunId: boolean; hasFinished: boolean };

/** Updates what the frames tell: the run id the server announced, and whether the turn ran to its end. */
const learnFromFrames = (lines: string[], threadId: string, progress: TurnProgress) => {
  for (const line of lines) {
    const part = parseFrame(line);
    if (!part) continue;
    if (part.type === 'finish') progress.hasFinished = true;
    if (!progress.isLearningRunId) continue;
    if (isDurableRunDataPart(part)) adoptAnnouncedRunId(threadId, part.data.runId);
    if (isDurableRunDataPart(part) || isPastAnnouncement(part.type)) progress.isLearningRunId = false;
  }
};

/**
 * Reads the turn's copy to its end. A send's copy also stores the run id the server announced, and a
 * turn that finishes drops its run: the left thread then opens on its history instead of a resume.
 */
const readTurn = async (stream: ReadableStream<Uint8Array>, threadId: string, isLearningRunId: boolean) => {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  const progress: TurnProgress = { isLearningRunId, hasFinished: false };
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const lines = (buffer + decoder.decode(value, { stream: true })).split('\n');
      buffer = lines.pop() ?? '';
      learnFromFrames(lines, threadId, progress);
    }
    if (progress.hasFinished) forgetDurableRun(threadId);
  } catch {
    // Aborted or cut: the run stays stored, to resume on the next visit.
  } finally {
    void reader.cancel().catch(() => undefined);
  }
};

/** The caller's copy of the body, ended with an AbortError the moment the caller aborts. */
const endOnAbort = (stream: ReadableStream<Uint8Array>, signal: AbortSignal | null | undefined) => {
  const reader = stream.getReader();
  return new ReadableStream<Uint8Array>({
    start(controller) {
      const onAbort = () => {
        controller.error(abortError());
        void reader.cancel().catch(() => undefined);
      };
      if (signal?.aborted) onAbort();
      else signal?.addEventListener('abort', onAbort, { once: true });
    },
    async pull(controller) {
      try {
        const { done, value } = await reader.read();
        if (done) controller.close();
        else controller.enqueue(value);
      } catch (error) {
        controller.error(error);
      }
    },
    cancel: (reason) => reader.cancel(reason),
  });
};

/**
 * A turn's request outlives the caller's abort. `useChat` aborts it when the user switches threads:
 * cut there, a send that hadn't seen `data-durable-run` would leave a run nothing can resume, and the
 * thread would stop showing it is still answering. The caller still sees the abort at once: its copy
 * of the body ends, so the old chat instance stops feeding the callbacks of the new one.
 */
const followTurn = (threadId: string, callerSignal: AbortSignal | null | undefined, request: (signal: AbortSignal) => Promise<Response>, kind: TurnSettled['kind']) => {
  const network = new AbortController();
  trackTurn(threadId, network);
  let detachedTimer: ReturnType<typeof setTimeout> | undefined;
  const onCallerAbort = () => {
    // Caps a left thread's read: a turn that never ends must not hold the connection open.
    detachedTimer = setTimeout(() => network.abort(), DETACHED_TURN_MAX_MS);
  };
  callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
  const settle = () => {
    clearTimeout(detachedTimer);
    callerSignal?.removeEventListener('abort', onCallerAbort);
    untrackTurn(threadId, network, kind);
  };

  const pending = request(network.signal).then(
    (response) => {
      if (!(response.ok && response.body)) {
        settle();
        return response;
      }
      const [forCaller, forTurn] = response.body.tee();
      void readTurn(forTurn, threadId, kind === 'send').finally(settle);
      return new Response(endOnAbort(forCaller, callerSignal), { status: response.status, statusText: response.statusText, headers: response.headers });
    },
    (error) => {
      settle();
      throw error;
    }
  );
  if (!callerSignal) return pending;
  return new Promise<Response>((resolve, reject) => {
    if (callerSignal.aborted) reject(abortError());
    callerSignal.addEventListener('abort', () => reject(abortError()), { once: true });
    pending.then(resolve, reject);
  });
};

const isFreshRun = (threadId: string) => {
  const startedAt = readDurableRun(threadId)?.startedAt;
  return startedAt !== undefined && Date.now() - startedAt < RESUME_RETRY_WINDOW_MS;
};

const fetchResume = async (input: RequestInfo | URL, init: RequestInit, threadId: string, send: typeof fetch) => {
  for (let attempt = 1; ; attempt++) {
    const response = await send(input, init);
    if (!isStartingRun(response.status) || attempt >= RESUME_MAX_ATTEMPTS || !isFreshRun(threadId)) return response;
    // The server may not have registered the just-sent run yet (404/410): ask again after a pause.
    await new Promise((resolve) => setTimeout(resolve, RESUME_RETRY_DELAY_MS));
  }
};

/**
 * `DefaultChatTransport` options that point `resumeStream` at the stored run of the thread. Callers
 * only resume when a run is stored, so the SDK's default `/chat/:agent/:id/stream` URL is never used.
 */
export const durableResumeTransportOptions = ({ baseUrl, agentName, threadId, fetch: send }: DurableResumeOptions) => ({
  prepareReconnectToStreamRequest: ({ id }: { id: string }) => {
    const runId = readDurableRun(id)?.runId;
    return runId ? { api: durableRunStreamUrl(baseUrl, agentName, runId) } : {};
  },
  fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
    if (init?.method === 'POST') return followTurn(threadId, init.signal, (signal) => send(input, { ...init, signal }), 'send');
    // Only the resume request is a GET; any failure there falls back to the thread history silently.
    if (init?.method !== 'GET') return send(input, init);
    try {
      const response = await followTurn(threadId, init.signal, (signal) => fetchResume(input, { ...init, signal }, threadId, send), 'resume');
      return silenceResumeFailure(response, threadId);
    } catch (error) {
      // Leaving mid-resume is not a failed resume: the run stays stored for the next visit.
      if (init.signal?.aborted) throw error;
      return silenceResumeFailure(new Response(null, { status: 503 }), threadId);
    }
  },
});

const textOf = (message: ChatMessage) =>
  message.parts
    .map((part) => (part.type === 'text' && 'text' in part && typeof part.text === 'string' ? part.text : ''))
    .join('')
    .trim();

/**
 * History to show before replaying the run: everything up to the user message that started it. The
 * replay rebuilds the assistant answer from `start`, so a partial answer already persisted after that
 * message is dropped (the SDK would append the replay onto it), and the user message is re-appended
 * when history doesn't hold it yet. Mastra may not keep the client id, so the text also matches.
 */
export const messagesBeforeResume = <M extends ChatMessage>(history: M[], userMessage: M): M[] => {
  const userText = textOf(userMessage);
  const lastUserIndex = history.findLastIndex((message) => message.role === 'user');
  const lastUser = history[lastUserIndex];
  const isPersisted = lastUser !== undefined && (lastUser.id === userMessage.id || (userText.length > 0 && textOf(lastUser) === userText));
  return isPersisted ? history.slice(0, lastUserIndex + 1) : [...history, userMessage];
};
